import { useEffect, useId, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useTranslate } from '@tolgee/react'
import { ErrorCode, type PartnerResponse } from '@kcm/contracts'
import { Button } from '@kcm/ui'
import { ApiError } from '../lib/api'
import { endpoints } from '../lib/endpoints'

/**
 * The partner code on step 1 (PLAN.md §19.1).
 *
 * Most referrals arrive as a tracked link and never touch this field. It exists
 * for the ones that do not — a partner's card, a seminar, a word-of-mouth
 * introduction — which the legacy funnel had no way to record at all, so those
 * registrations were attributed to nobody and paid nobody.
 *
 * Collapsed by default. It is optional, and an always-open box asking for a
 * code most people do not have reads as a field they have failed to fill in.
 *
 * Nothing here is trusted. The number is resolved server-side into the complete
 * partner/link pair the CRM needs, and only the resolved pair is filed; the
 * browser never supplies a partner id to the lead.
 */
export function PartnerCode() {
  const { t } = useTranslate()
  const inputId = useId()
  const [open, setOpen] = useState(false)
  const [code, setCode] = useState('')
  const [confirmed, setConfirmed] = useState<PartnerResponse | null>(null)

  // What the landing already knows, if anything. A failure is silence: this is
  // an optional field, and an error banner about a partner nobody mentioned
  // would be noise on the last screen of the account setup.
  const current = useQuery({
    queryKey: ['partner'],
    queryFn: ({ signal }) => endpoints.partner.current(signal),
    retry: false,
    staleTime: Infinity,
  })

  const landed = current.data?.partner ?? null
  useEffect(() => {
    if (!landed) return
    // Already resolved and already theirs — shown open, with the link id in the
    // field, so they can see who they are being credited to and change it.
    setConfirmed(landed)
    setCode(String(landed.referralLinkId))
    setOpen(true)
  }, [landed])

  const lookup = useMutation({
    mutationFn: () => endpoints.partner.lookup({ code: Number(code) }),
    onSuccess: (partner) => {
      setConfirmed(partner)
      setCode(String(partner.referralLinkId))
    },
  })

  const clear = useMutation({
    mutationFn: () => endpoints.partner.clear(),
    onSuccess: () => {
      setConfirmed(null)
      setCode('')
      setOpen(false)
      lookup.reset()
    },
  })

  const error = lookup.error instanceof ApiError ? lookup.error : null
  const message =
    error?.code === ErrorCode.PARTNER_NOT_FOUND || error?.code === ErrorCode.RATE_LIMITED
      ? error.message
      : error
        ? t('partner.unavailable')
        : undefined

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex min-h-[44px] items-center justify-center gap-2 rounded-lg border border-line-strong px-3 py-2 text-sm font-medium text-brand"
      >
        <PlusIcon />
        {t('partner.add')}
      </button>
    )
  }

  const apply = () => {
    if (code === '' || confirmed !== null || lookup.isPending) return
    lookup.mutate()
  }

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-line-strong p-3">
      <div className="flex items-center justify-between">
        <label htmlFor={inputId} className="flex items-center gap-2 text-sm font-medium text-ink">
          <TagIcon />
          {t('partner.label')}
        </label>
        {!confirmed && (
          <button
            type="button"
            aria-label={t('partner.close')}
            onClick={() => {
              setOpen(false)
              setCode('')
              lookup.reset()
            }}
            className="flex h-11 w-11 items-center justify-center rounded-lg text-muted"
          >
            <CloseIcon />
          </button>
        )}
      </div>

      <input
        id={inputId}
        value={code}
        // Every partner code is a number on both sides of the pair, so the
        // filter is the validation: there is no such thing as a valid code
        // with a letter in it, and a numeric keypad on mobile is free.
        inputMode="numeric"
        autoComplete="off"
        enterKeyHint="done"
        aria-invalid={message ? true : undefined}
        aria-describedby={message ? `${inputId}-error` : undefined}
        onChange={(e) => {
          setCode(e.target.value.replace(/\D/g, '').slice(0, 10))
          if (confirmed) setConfirmed(null)
          lookup.reset()
        }}
        // This field lives inside the sign-up form, so a bare Enter here would
        // submit the whole step instead of checking the code.
        onKeyDown={(e) => {
          if (e.key !== 'Enter') return
          e.preventDefault()
          apply()
        }}
        className={`min-h-[44px] w-full rounded-lg border bg-white px-3 py-2 text-ink placeholder:text-muted-soft ${
          message ? 'border-danger' : 'border-line-strong'
        }`}
      />

      {message && (
        <p id={`${inputId}-error`} role="alert" className="text-sm text-danger">
          {message}
        </p>
      )}

      {confirmed ? (
        <div className="flex items-start justify-between gap-2 rounded-lg bg-surface-alt px-3 py-2 text-sm">
          <div>
            <p className="font-medium text-ink">{t('partner.referredBy')}</p>
            <p className="text-muted">{`${t('partner.partnerId')}: ${confirmed.partnerId}`}</p>
            {confirmed.partnerCountry && (
              <p className="text-muted">{`${t('partner.country')}: ${confirmed.partnerCountry}`}</p>
            )}
          </div>
          <button
            type="button"
            onClick={() => clear.mutate()}
            disabled={clear.isPending}
            className="min-h-[44px] shrink-0 text-sm font-medium text-brand underline disabled:opacity-60"
          >
            {t('partner.remove')}
          </button>
        </div>
      ) : (
        <Button
          type="button"
          variant="secondary"
          loading={lookup.isPending}
          disabled={code === ''}
          onClick={apply}
        >
          {t('partner.confirm')}
        </Button>
      )}
    </div>
  )
}

/* Inline SVG rather than an icon font or an emoji: one immutable bundle serves
   every site, and `currentColor` follows the per-site brand token. */

function PlusIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M8 3.5v9M3.5 8h9"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  )
}

function TagIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden="true"
      className="shrink-0 text-muted"
    >
      <path
        d="M10.6 2.5H16a1.5 1.5 0 0 1 1.5 1.5v5.4a1.5 1.5 0 0 1-.44 1.06l-6.1 6.1a1.5 1.5 0 0 1-2.12 0l-5.4-5.4a1.5 1.5 0 0 1 0-2.12l6.1-6.1a1.5 1.5 0 0 1 1.06-.44Z"
        stroke="currentColor"
        strokeWidth="1.4"
      />
      <circle cx="13.4" cy="6.6" r="1.15" fill="currentColor" />
    </svg>
  )
}

function CloseIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M4 4l8 8M12 4l-8 8"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  )
}
